// Universal assembly template ("fiche de montage") — ONE sheet printed once and
// reused for every Storiz hardcover. It is NOT per-order. The bindery lays the
// printed cover wrap on top of this fiche (the fiche is BIGGER than the vinyl —
// 20 mm margin all around — so the fold lines, 45° corner snips and spine-centre
// axis stick OUT past the cover and stay visible for alignment). The marks are
// drawn at the SAME positions as the guide lines on the cover (see
// drawCoverGuides in pdfGenerator.ts) — both read the SAME computeTemplateLayout
// + PrintSettings, so they can never drift.
//
// Geometry derives from the editable PrintSettings (board / sheet / spine / hinge
// / thickness). pdf-lib only — renders in the app and in Node. True 1:1 mm scale
// (MediaBox in real millimetres) for large-format Roland vinyl output.
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const MM = 2.8346; // mm → pt

export interface PrintSettings {
  /** Printed vinyl wrap sheet (mm). DERIVED from the case geometry via
   *  computeVinylSizeMm — do not edit directly; it recalculates when the spine,
   *  board, gap, turn-in or edge change. Kept on the struct as a cache (and so
   *  the fiche can draw on a bigger sheet by overriding it). */
  sheetWmm: number;
  sheetHmm: number;
  /** Each rigid board (front = back), mm. */
  boardWmm: number;
  boardHmm: number;
  /** Spine board width (mm) — default 10, adjustable. The vinyl WIDTH
   *  recalculates from it. */
  spineWmm: number;
  /** Hinge gap between each board and the spine (mm) — one each side. */
  jointGapMm: number;
  /** Board thickness, doubled (mm) — sets the 45° corner-snip setback. */
  boardThicknessMm: number;
  /** Turn-in: vinyl folded onto the inside of the board (mm) — all 4 edges. */
  turnInMm: number;
  /** Edge thickness allowance the vinyl wraps around (mm) — all 4 edges. */
  edgeMm: number;
  /** Safe zone for cutting/wrapping (mm) — all 4 edges. Adjustable. */
  safeZoneMm: number;
}

/** Vinyl wrap size from the case spec — NEVER hardcoded; recalculates when any
 *  input changes (e.g. a wider spine or safe zone grows the sheet):
 *    width  = 2·board + 2·gap + spine + 2·edge + 2·turn-in + 2·safe-zone
 *    height = board + 2·edge + 2·turn-in + 2·safe-zone
 *  Defaults (board 200, gap 8, spine 10, edge 3, turn-in 20, safe 5) → 482 × 359 mm. */
export function computeVinylSizeMm(s: Pick<PrintSettings,
  'boardWmm' | 'boardHmm' | 'spineWmm' | 'jointGapMm' | 'turnInMm' | 'edgeMm' | 'safeZoneMm'>,
): { wmm: number; hmm: number } {
  const margin = 2 * (s.edgeMm + s.turnInMm + s.safeZoneMm);  // per axis, both sides
  return {
    wmm: 2 * s.boardWmm + 2 * s.jointGapMm + s.spineWmm + margin,
    hmm: s.boardHmm + margin,
  };
}

/** Return settings with sheetWmm/sheetHmm recomputed from the case spec, so a
 *  stale stored sheet (or a changed spine) can never desync the vinyl size. */
export function withDerivedVinyl(s: PrintSettings): PrintSettings {
  const v = computeVinylSizeMm(s);
  return { ...s, sheetWmm: v.wmm, sheetHmm: v.hmm };
}

// Physical case spec (mm). The vinyl sheet is DERIVED from these, not stored.
const CASE_SPEC = {
  boardWmm: 200,        // visible front/back board (front + back = 400)
  boardHmm: 303,
  spineWmm: 10,         // default spine board; adjustable parameter
  jointGapMm: 8,        // hinge gap each side of the spine
  boardThicknessMm: 3,  // doubled board thickness
  turnInMm: 20,         // turn-in onto the inside of the board, all 4 edges
  edgeMm: 3,            // edge thickness allowance, all 4 edges
  safeZoneMm: 5,        // cutting/wrapping safe zone, all 4 edges; adjustable
} as const;

const _DEFAULT_VINYL = computeVinylSizeMm(CASE_SPEC);   // → 482 × 359 mm
export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  ...CASE_SPEC,
  sheetWmm: _DEFAULT_VINYL.wmm,
  sheetHmm: _DEFAULT_VINYL.hmm,
};

export interface TemplateLayout {
  /** Board fore-edge turn-in (mm) — left/right. */
  turnInXmm: number;
  /** Board head/tail turn-in (mm) — top/bottom. */
  turnInYmm: number;
  /** Hinge groove each side of the spine (mm). */
  hingeMm: number;
  // positions in pt (origin bottom-left)
  spineColL: number; spineColR: number;
  backBoardL: number; backBoardR: number;
  frontBoardL: number; frontBoardR: number;
  spineBoardL: number; spineBoardR: number;
  boardBottom: number; boardTop: number;
  sheetCx: number;
  /** False when the boards don't fit the sheet. */
  fits: boolean;
}

export function computeTemplateLayout(s: PrintSettings): TemplateLayout {
  const W = s.sheetWmm * MM, H = s.sheetHmm * MM;
  const cx = W / 2;
  // Printed spine COLUMN = spine board + 2× hinge (derived, so any board / spine
  // / hinge size flows straight through — "handles all sizes"). Its edges are
  // the cover fold lines.
  const spineColMm = s.spineWmm + 2 * s.jointGapMm;
  const spineColL = cx - (spineColMm / 2) * MM;
  const spineColR = cx + (spineColMm / 2) * MM;
  const boardW = s.boardWmm * MM, boardH = s.boardHmm * MM;
  const backBoardR = spineColL;
  const backBoardL = backBoardR - boardW;
  const frontBoardL = spineColR;
  const frontBoardR = frontBoardL + boardW;
  const spineBoardL = cx - (s.spineWmm / 2) * MM;
  const spineBoardR = cx + (s.spineWmm / 2) * MM;
  const boardBottom = (H - boardH) / 2;
  const boardTop = boardBottom + boardH;
  const hingeMm = s.jointGapMm;
  return {
    turnInXmm: backBoardL / MM,
    turnInYmm: boardBottom / MM,
    hingeMm,
    spineColL, spineColR,
    backBoardL, backBoardR, frontBoardL, frontBoardR,
    spineBoardL, spineBoardR, boardBottom, boardTop, sheetCx: cx,
    fits: backBoardL >= 0 && boardBottom >= 0,
  };
}

/** Fiche grows past the vinyl by this margin on EVERY side so the guide lines
 *  stick out beyond the cover when it's laid on top. */
const MARGIN_MM = 20;

const fmtMm = (mm: number) => `${Math.round(mm * 10) / 10} mm`;

export async function generateAssemblyTemplatePdf(
  settings: PrintSettings,
  label?: string,
): Promise<Uint8Array> {
  // Normalise so the vinyl footprint is the DERIVED size (never a stale stored
  // sheet) — the fiche then grows past it by MARGIN_MM on every side.
  const s = withDerivedVinyl(settings);
  const spineColMm = s.spineWmm + 2 * s.jointGapMm;
  const cutMm = 1.5 * s.boardThicknessMm; // 45° corner-cut setback (matches the cover)

  // The vinyl cover comes off the Roland at exactly sheetWmm × sheetHmm. The
  // fiche is that + MARGIN_MM on every side. Geometry is centred, so the board
  // zones sit at the SAME place relative to the spine on the cover and the fiche
  // — the worker centres the cover on the fiche and every mark lines up.
  const coverWmm = s.sheetWmm, coverHmm = s.sheetHmm;
  const ficheWmm = coverWmm + 2 * MARGIN_MM;
  const ficheHmm = coverHmm + 2 * MARGIN_MM;

  // ONE source of truth: the SAME computeTemplateLayout the cover uses. Run on
  // the bigger fiche sheet for the DRAW coordinates...
  const L = computeTemplateLayout({ ...s, sheetWmm: ficheWmm, sheetHmm: ficheHmm });
  // ...and on the real cover sheet for the printed turn-in VALUES (board edge →
  // vinyl edge ≈ 17.5 / 13 mm), which is what the worker measures — not the
  // fiche's outer margin.
  const cover = computeTemplateLayout(s);
  const turnInXmm = cover.turnInXmm;
  const turnInYmm = cover.turnInYmm;

  const doc = await PDFDocument.create();
  const W = ficheWmm * MM, H = ficheHmm * MM;
  const margin = MARGIN_MM * MM;
  const page = doc.addPage([W, H]);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  const ink = rgb(0.1, 0.1, 0.12);
  const board = rgb(0.12, 0.32, 0.7);
  const black = rgb(0, 0, 0);
  const axis = rgb(0.82, 0.16, 0.16);
  const coverEdge = rgb(0.6, 0.42, 0.12);
  const dim = rgb(0.4, 0.4, 0.46);
  const fill = rgb(0.93, 0.95, 0.99);
  const LW = 0.9; // ≥ 0.85 pt (0.3 mm) so lines never drop out on Roland vinyl

  const boardH = L.boardTop - L.boardBottom;
  // Cover (vinyl) footprint — centred on the fiche.
  const coverX = margin, coverY = margin, coverW = coverWmm * MM, coverH = coverHmm * MM;
  const coverR = coverX + coverW, coverT = coverY + coverH;

  // ── Fiche sheet outline ────────────────────────────────────────────────────
  page.drawRectangle({ x: 0.5, y: 0.5, width: W - 1, height: H - 1, borderColor: ink, borderWidth: LW });

  // ── Edge rulers (top + left): ticks every 10 mm, numbers every 50 mm, 0 at the
  // top-left corner so the worker can measure straight off the sheet. ─────────
  const RULER = 7 * MM;
  for (let xmm = 0; xmm <= ficheWmm + 0.01; xmm += 10) {
    const x = xmm * MM, major = xmm % 50 === 0, len = major ? RULER : RULER * 0.5;
    page.drawLine({ start: { x, y: H }, end: { x, y: H - len }, thickness: major ? LW : 0.85, color: ink });
    if (major) page.drawText(`${xmm}`, { x: x + 1.5, y: H - RULER - 8, size: 6, font, color: dim });
  }
  for (let ymm = 0; ymm <= ficheHmm + 0.01; ymm += 10) {
    const y = H - ymm * MM, major = ymm % 50 === 0, len = major ? RULER : RULER * 0.5;
    page.drawLine({ start: { x: 0, y }, end: { x: len, y }, thickness: major ? LW : 0.85, color: ink });
    if (major) page.drawText(`${ymm}`, { x: len + 2, y: y - 2, size: 6, font, color: dim });
  }

  // ── Cover (vinyl) footprint — the worker centres the printed cover here ─────
  page.drawRectangle({ x: coverX, y: coverY, width: coverW, height: coverH, borderColor: coverEdge, borderWidth: LW, borderDashArray: [6, 3] });
  page.drawText('BORD DU VINYLE — poser la couverture ici (centrée)', { x: coverX + 4, y: coverT - 11, size: 7, font: fontB, color: coverEdge });

  // ── Boards (centred) + name + dimension labels ─────────────────────────────
  const boards = [
    { x0: L.backBoardL, x1: L.backBoardR, label: 'CARTON ARRIÈRE' },
    { x0: L.frontBoardL, x1: L.frontBoardR, label: 'CARTON AVANT' },
  ];
  for (const b of boards) {
    page.drawRectangle({ x: b.x0, y: L.boardBottom, width: b.x1 - b.x0, height: boardH, color: fill, borderColor: board, borderWidth: LW * 1.5 });
    const cxB = (b.x0 + b.x1) / 2, cyB = (L.boardBottom + L.boardTop) / 2;
    const nameW = fontB.widthOfTextAtSize(b.label, 11);
    page.drawText(b.label, { x: cxB - nameW / 2, y: cyB + 3, size: 11, font: fontB, color: board });
    const dimTxt = `${fmtMm(s.boardWmm)} × ${fmtMm(s.boardHmm)}`;
    const dimW = font.widthOfTextAtSize(dimTxt, 9);
    page.drawText(dimTxt, { x: cxB - dimW / 2, y: cyB - 13, size: 9, font, color: dim });
  }
  // Spine board + width label.
  page.drawRectangle({ x: L.spineBoardL, y: L.boardBottom, width: L.spineBoardR - L.spineBoardL, height: boardH, color: fill, borderColor: board, borderWidth: LW * 1.5 });
  page.drawText('DOS', { x: L.sheetCx - fontB.widthOfTextAtSize('DOS', 8) / 2, y: L.boardBottom + 12, size: 8, font: fontB, color: board });
  const spineTxt = fmtMm(s.spineWmm);
  page.drawText(spineTxt, { x: L.sheetCx - font.widthOfTextAtSize(spineTxt, 7) / 2, y: L.boardBottom + 3, size: 7, font, color: dim });

  // ── Fold lines at the spine-column edges — FULL fiche height so they stick out
  // past the cover top + bottom. ─────────────────────────────────────────────
  for (const fx of [L.spineColL, L.spineColR]) {
    page.drawLine({ start: { x: fx, y: 0 }, end: { x: fx, y: H }, thickness: LW, color: dim, dashArray: [5, 3] });
  }
  // Hinge labels (one each side of the spine board).
  const hingeTxt = fmtMm(L.hingeMm);
  page.drawText(hingeTxt, { x: (L.spineColL + L.spineBoardL) / 2 - font.widthOfTextAtSize(hingeTxt, 6) / 2, y: L.boardTop + 5, size: 6, font, color: dim });
  page.drawText(hingeTxt, { x: (L.spineBoardR + L.spineColR) / 2 - font.widthOfTextAtSize(hingeTxt, 6) / 2, y: L.boardTop + 5, size: 6, font, color: dim });

  // ── Spine-centre AXIS — full fiche height + bold edge ticks ────────────────
  page.drawLine({ start: { x: L.sheetCx, y: 0 }, end: { x: L.sheetCx, y: H }, thickness: LW, color: axis, dashArray: [5, 4], opacity: 0.85 });
  const TICK = 7 * MM;
  page.drawLine({ start: { x: L.sheetCx, y: 0 }, end: { x: L.sheetCx, y: TICK }, thickness: 1.6, color: axis });
  page.drawLine({ start: { x: L.sheetCx, y: H }, end: { x: L.sheetCx, y: H - TICK }, thickness: 1.6, color: axis });
  page.drawText('AXE DU DOS', { x: L.sheetCx + 3, y: H / 2 + 2, size: 7, font: fontB, color: axis });

  // ── 45° corner cuts at the 4 fore-edge board corners — dashed black @ 45 % ──
  const SNIP = 8 * MM, SNIP_D = SNIP / Math.SQRT2;
  const cutTxt = fmtMm(cutMm);
  const corner = (x: number, y: number, dx: number, dy: number) => {
    page.drawLine({ start: { x, y }, end: { x: x + dx * SNIP_D, y: y + dy * SNIP_D }, thickness: LW, color: black, opacity: 0.45, dashArray: [2, 2] });
    page.drawText(cutTxt, {
      x: x + dx * SNIP_D + (dx > 0 ? 2 : -font.widthOfTextAtSize(cutTxt, 5.5) - 2),
      y: y + dy * SNIP_D + (dy > 0 ? 1 : -6),
      size: 5.5, font, color: black, opacity: 0.6,
    });
  };
  corner(L.backBoardL, L.boardBottom, -1, -1);
  corner(L.backBoardL, L.boardTop, -1, +1);
  corner(L.frontBoardR, L.boardBottom, +1, -1);
  corner(L.frontBoardR, L.boardTop, +1, +1);

  // ── Turn-in dimension labels (board edge → vinyl edge = the printed turn-in) ─
  const annot = (text: string, ax: number, ay: number) => page.drawText(text, { x: ax, y: ay, size: 8, font, color: dim });
  const txX = fmtMm(turnInXmm), txY = fmtMm(turnInYmm);
  annot(txX, (coverX + L.backBoardL) / 2 - font.widthOfTextAtSize(txX, 8) / 2, H / 2);   // left turn-in
  annot(txX, (L.frontBoardR + coverR) / 2 - font.widthOfTextAtSize(txX, 8) / 2, H / 2);  // right turn-in
  annot(txY, L.sheetCx + 16, (L.boardTop + coverT) / 2 - 3);                              // top turn-in
  annot(txY, L.sheetCx + 16, (coverY + L.boardBottom) / 2 - 3);                           // bottom turn-in

  // ── HAUT markers + up-arrow over each board ────────────────────────────────
  const drawHaut = (cxmm: number) => {
    const baseY = L.boardTop + 16;
    page.drawText('HAUT', { x: cxmm - fontB.widthOfTextAtSize('HAUT', 10) / 2, y: baseY, size: 10, font: fontB, color: ink });
    const ax = cxmm + fontB.widthOfTextAtSize('HAUT', 10) / 2 + 6, ay1 = baseY + 9;
    page.drawLine({ start: { x: ax, y: baseY + 1 }, end: { x: ax, y: ay1 }, thickness: LW, color: ink });
    page.drawLine({ start: { x: ax, y: ay1 }, end: { x: ax - 2.5, y: ay1 - 3 }, thickness: LW, color: ink });
    page.drawLine({ start: { x: ax, y: ay1 }, end: { x: ax + 2.5, y: ay1 - 3 }, thickness: LW, color: ink });
  };
  drawHaut((L.backBoardL + L.backBoardR) / 2);
  drawHaut((L.frontBoardL + L.frontBoardR) / 2);

  // ── Title + sizes (bottom margin, clear of the boards + rulers) ────────────
  page.drawText('FICHE DE MONTAGE — poser la couverture imprimée au centre, aligner les traits', {
    x: RULER + 6, y: 18, size: 8, font: fontB, color: ink,
  });
  page.drawText(`Feuille ${fmtMm(ficheWmm)} × ${fmtMm(ficheHmm)} · vinyle ${fmtMm(coverWmm)} × ${fmtMm(coverHmm)} · dos ${fmtMm(s.spineWmm)} (colonne ${fmtMm(spineColMm)})`, {
    x: RULER + 6, y: 7, size: 7, font, color: dim,
  });
  if (label) page.drawText(label, { x: W - 130, y: 7, size: 8, font: fontB, color: ink });
  if (!L.fits) {
    page.drawText('⚠ Les cartons ne tiennent pas sur la feuille', { x: L.sheetCx - 90, y: H / 2 - 30, size: 12, font: fontB, color: axis });
  }

  return doc.save();
}
